import { ProjectJSON } from '@projects/models/project';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { createProject as createProjectAction, resetCreateProjectRequest } from 'src/actions/projects';
import { isNotAllowedError } from 'src/errors/common';
import { isFreeTrialLimitError, isSubscriptionLimitError } from 'src/errors/projects';
import { actionResultIsError } from 'src/lib/reduxUtils';
import { ERROR_DEFAULT, ERROR_NAME_NOT_ALLOWED } from 'src/messages/projects';

type UseCreateProjectParams = {
	onProjectCreationFailed?: () => void,
	onProjectCreationSuccess?: ( projectID: number ) => void,
	showSuccessMessage?: boolean
};

export type CreateProjectFormProperties = {
	workspaceID: number,
	name: string,
	description: string
};

type CreateProjectResult = {
	payload: ProjectJSON
};

const useCreateProject = ( {
	onProjectCreationFailed,
	onProjectCreationSuccess,
	showSuccessMessage = true
}: UseCreateProjectParams ) => {
	const dispatch = useDispatch();
	const snackbarSystem = useSnackbarSystem();

	const onSuccess = useCallback(
		( project: ProjectJSON ) => {
			dispatch( resetCreateProjectRequest() );
			if ( showSuccessMessage ) {
				snackbarSystem.showSuccessMessage( { title: 'Project created successfully' } );
			}
			onProjectCreationSuccess?.( project.id );
		},
		[ dispatch, onProjectCreationSuccess, snackbarSystem, showSuccessMessage ]
	);

	const onError = useCallback(
		( error: unknown ) => {
			const payload = ( error as Record<string, unknown> )?.payload || error;
			if ( isSubscriptionLimitError( payload ) || isFreeTrialLimitError( payload ) ) {
				return onProjectCreationFailed?.();
			}
			const message = isNotAllowedError( payload )
				? ERROR_NAME_NOT_ALLOWED
				: ERROR_DEFAULT;
			return snackbarSystem.showErrorMessage( { title: message } );
		},
		[ onProjectCreationFailed, snackbarSystem ]
	);

	const createProject = useCallback(
		( properties: CreateProjectFormProperties ): Promise<CreateProjectResult> => (
			dispatch( createProjectAction( properties ) ) as unknown as Promise<CreateProjectResult>
		)
			.then( ( result: CreateProjectResult ) => {
				if ( !actionResultIsError( result ) ) {
					onSuccess( result.payload );
				} else {
					onError( result );
				}
				return result;
			} )
			.catch( ( error: unknown ) => {
				onError( error );
				throw error;
			} ),
		[ dispatch, onError, onSuccess ]
	);

	return createProject;
};

export default useCreateProject;
